import React from "react";
import styles from "./LearningCard.module.scss";
import { Box, Chip, Typography } from "@mui/material";
import CustomButton from "../../../../components/v2/custom-button/CustomButton";
import { useTranslation } from "react-i18next";
import NoImageThumbnail from "../../../../assets/icons/learning_no_image.svg";
import EducationContentIcons from "../../../../components/v2/education-content-icons/EducationContentIcons";
import CheckedCircleIcon from "../../../../components/icon-components/CheckedCircleIcon";

interface LearningCardProps {
  imageSrc: string;
  imageAlt?: string;
  isNew?: boolean;
  duration: string;
  title: string;
  height?: string;
  width?: string;
  contentType: string;
  activityPoints: number;
  activityCompletionStatus: string;
  onButtonClick: () => void;
}

const LearningCard: React.FC<LearningCardProps> = ({
  imageSrc,
  imageAlt = "Thumbnail",
  isNew = false,
  duration,
  title,
  height,
  width,
  contentType,
  activityPoints,
  activityCompletionStatus,
  onButtonClick,
}) => {
  const { t } = useTranslation();
  const pendingStatuses = [
    t("inProgress"),
    t("pending"),
    t("taskPending"),
    t("notCompleted"),
    t("initiated"),
    t("notStarted"),
    "",
  ];

  const isCompleted = !pendingStatuses.includes(activityCompletionStatus);

  return (
    <Box className={styles.cardContainer} maxWidth={width} height={height}>
      <Box
        className={`${styles.imageSection} ${
          imageSrc ? styles.withThumbnail : ""
        }`}
      >
        {isNew && (
          <Chip label={t("educationContent.new")} className={styles.newChip} />
        )}
        <img
          src={imageSrc || NoImageThumbnail}
          alt={imageAlt}
          className={imageSrc ? styles.thumbnailImage : styles.noThumbnailImage}
        />
        {isCompleted ? (
          <Box className={styles.completedBox}>
            <CheckedCircleIcon width={20} height={20} variant={"completed"} />
          </Box>
        ) : (
          <Box className={styles.activityPointsBox}>
            <Typography variant="body2" fontWeight={"bold"}>
              + {activityPoints}
            </Typography>
          </Box>
        )}
      </Box>

      <Box className={styles.cardContent}>
        <Box display="flex" alignItems="center" className={styles.videoInfo}>
          <EducationContentIcons
            type={contentType}
            height={16}
            width={17}
            color="#4E7E6E"
          />
          <Typography variant="body2" className={styles.videoText}>
            {contentType === "videos" && (
              <>
                {t("video")}
                {duration ? ` | ${duration} ${t("educationContent.min")}` : ""}
              </>
            )}
            {contentType === "articles" && t("article")}
            {contentType === "URL" && t("URL")}
          </Typography>
        </Box>
        <Box sx={{ height: "80%" }}>
          <Typography variant="body1" className={styles.titleText}>
            {title}
          </Typography>
          <Box sx={{ height: "60%" }} pt={1}>
            <CustomButton
              variant="outlined"
              text={
                {
                  videos: t("carePlan.watch"),
                  articles: t("carePlan.read"),
                  URL: t("educationContent.open"),
                }[contentType] || t("educationContent.open")
              }
              onClick={onButtonClick}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LearningCard;
